import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./dashboard.css";
import { Box, Link } from "@mui/material";
import { NavBar } from "../../../../components/NavBar/NavBar";
import { useNavigate } from "react-router-dom";
import { Tabs } from "../../../../components/DashboardTabs/Tabs";
import {
  selectors,
  actions,
} from "../../../../redux/features/dashboards/storeDashboardSlice";
import { useTabsByWorkStreamQuery } from "../../../../hooks/useTabsByWorkStreamQuery";
import { useTabContentQuery } from "../../../../hooks/useTabContentQuery";
import { useAddDashboardTabMutation } from "../../../../hooks/useAddDashboardTabMutation";
import { useAddPanelToTabMutation } from "../../../../hooks/useAddPanelToTabMutation";
import { useUpdatePanelsPositionsMutation } from "../../../../hooks/useUpdatePanelsPositionsMutation";
import { useGridStackContext } from "../../../../contexts/grid-stack-context";
import { useDeleteDashboardPanelMutation } from "../../../../hooks/useDeleteDashboardPanelMutation";
import { useDeleteDashboardTabMutation } from "../../../../hooks/useDeleteDashboardTabMutation";
import { actions as loadingActions } from "../../../../redux/features/loader/loaderSlice";
import PanelFilter from "../controlPanel/PanelFilter";
import { ReduxLoader } from "../../../../components/Loader/ReduxLoader";
import { useUpdateDashboardTabMutation } from "../../../../hooks/useUpdateDashboardTabMutation";

const StoreDashboardModule = ({ navBarInfo }) => {
  const dispatch = useDispatch();
  const { instance, events } = useGridStackContext();
  const activeTabId = useSelector(selectors.selectStoreDashboardActiveTabId);
  const [isPanelFilterOpen, setIsPanelFilterOpen] = useState(false);

  const requestHandlers = {
    onLoading: loadingActions.setLoader,
    onError: actions.setError,
  };

  const tabs = useTabsByWorkStreamQuery({
    ...requestHandlers,
    stream: "stores",
    selectors: {
      tabs: selectors.selectStoreDashboardTabs,
      isLoading: selectors.selectIsStoreDashboardLoading,
    },
    onResponse: ({ id, tabs }) => {
      dispatch(actions.setId(id));
      dispatch(actions.setTabs(tabs));
    },
  });

  const { TabContent, props } = useTabContentQuery({
    ...requestHandlers,
    stream: "stores",
    selectors: {
      tabId: selectors.selectStoreDashboardActiveTabId,
      content: selectors.selectStoreDashboardTabContent,
      isLoading: selectors.selectIsStoreDashboardLoading,
    },
    actions: {
      upgradeToGridstack: (...args) =>
        dispatch(actions.upgradePanelContentToGridStack(...args)),
    },
    onResponse: (panelContent) => {
      dispatch(actions.setTabContentById(panelContent));
      if (panelContent) {
        events.onPanelsFetch(panelContent);
      }
    },
  });

  const { addWorkStreamTab } = useAddDashboardTabMutation({
    ...requestHandlers,
    selectors: {
      id: selectors.selectStoreDashboardId,
      tabs: selectors.selectStoreDashboardTabs,
    },
    onResponse: actions.addTab,
  });

  const { deleteDashboardTab } = useDeleteDashboardTabMutation({
    ...requestHandlers,
    onResponse: actions.deleteTab,
  });

  const { updateDashboardTab } = useUpdateDashboardTabMutation({
    ...requestHandlers,
    selectors: {
      tabs: selectors.selectStoreDashboardTabs,
    },
    onResponse: actions.updateTab,
  });

  const { addPanelInTab } = useAddPanelToTabMutation({
    ...requestHandlers,
    selector: selectors.selectStoreDashboardActiveTabId,
    onResponse: (e) => dispatch(actions.upgradePanelContentToGridStack(e)),
  });

  const { deleteDashboardPanel } =
    useDeleteDashboardPanelMutation(requestHandlers);

  const { updatePositions } = useUpdatePanelsPositionsMutation({
    ...requestHandlers,
    selector: selectors.selectStoreDashboardActiveTabId,
  });

  const onTabChange = (newtab) => {
    dispatch(actions.setActiveTabId(newtab));
  };

  const onTabReset = () => {
    dispatch(actions.resetTabContent());
  };

  const handleTogglePanelFilter = () => {
    setIsPanelFilterOpen(!isPanelFilterOpen);
  };

  const handleClosePanelFilter = () => {
    setIsPanelFilterOpen(false);
  };

  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem("mod_id", "11");
  }, []);

  useEffect(() => {
    events.set.onAddPanel(addPanelInTab);
    events.set.onUpdatePanel(updatePositions);
    events.set.onDeletePanel(deleteDashboardPanel);
    events.set.onResetTabContent(onTabReset);
  }, [activeTabId]);

  return (
    <div className="dashboard">
      <NavBar
        hiddeBackButton={true}
        hideBreadcrumbs={true}
        dataBreadCrumbs={navBarInfo.dataBreadCrumbs}
        moduleIcon={navBarInfo.moduleIcon}
        moduleColor={navBarInfo.moduleColor}
        title={navBarInfo.title}
        menuTitle={navBarInfo.menuTitle}
        moduleId={navBarInfo.moduleId}
      />

      <section
        className={`dashboard-container ${
          isPanelFilterOpen ? "with-filter-panel" : ""
        }`}
      >
        <Tabs
          tabs={tabs}
          activeTabId={activeTabId}
          onTabChange={onTabChange}
          onAddTab={addWorkStreamTab}
          onDeleteTab={deleteDashboardTab}
          onUpdateTab={updateDashboardTab}
          onToggleFilterPanel={handleTogglePanelFilter}
        />
        <div className="dashboard-content-wrapper">
          <Box
            sx={{
              flexGrow: "1",
            }}
            className="box-style"
            role="tabpanel"
            id={`dashboard-${activeTabId}`}
            key={`dashboard-${activeTabId}`}
            aria-labelledby={`dashboard-${activeTabId}`}
          >
              <div ref={instance.ref} id="grid-stack-container-ref" />
              <ReduxLoader />
              <TabContent {...props} />
          </Box>
        </div>
        <PanelFilter
          open={isPanelFilterOpen}
          onClose={handleClosePanelFilter}
        />
      </section>
    </div>
  );
};

export default StoreDashboardModule;
